import { useCallback, useMemo } from 'react'

import { AuthState, AuthStatus, AuthUser, Session } from '../machine/types'
import { useAuthRuntime, useAuthState } from './AuthProvider'

export interface UseAuthValue {
    status: AuthStatus
    state: AuthState
    user: AuthUser | null
    roles: readonly string[]
    /** Airport IATA codes from the `groups` claim; `[]` when no session. */
    airports: readonly string[]
    isAuthenticated: boolean
    /** True while we're booting, logging in, or refreshing — UX may want a spinner. */
    isBusy: boolean
    /** The current session, if any. */
    session: Session | null
    /** Returns an access token, refreshing preemptively if near expiry. */
    getAccessToken: () => Promise<string | null>
    login: () => Promise<void>
    logout: () => Promise<void>
    /** Refreshes the access token (`force` supersedes an in-flight refresh); `null` on failure. */
    refresh: (options?: { force?: boolean }) => Promise<Session | null>
}

const sessionOf = (state: AuthState): Session | null => {
    if (state.tag === 'authenticated' || state.tag === 'refreshing') {
        return state.session
    }
    return null
}

const userOf = (state: AuthState): AuthUser | null => sessionOf(state)?.claims.user ?? null

const rolesOf = (state: AuthState): readonly string[] => sessionOf(state)?.claims.roles ?? []

const EMPTY_AIRPORTS: readonly string[] = Object.freeze([])

const airportsOf = (state: AuthState): readonly string[] =>
    sessionOf(state)?.claims.airports ?? EMPTY_AIRPORTS

const BUSY_STATES = new Set<AuthStatus>(['initializing', 'authenticating', 'refreshing'])

export const useAuth = (): UseAuthValue => {
    const runtime = useAuthRuntime()
    const state = useAuthState()

    const getAccessToken = useCallback(() => runtime.getAccessToken(), [runtime])
    const login = useCallback(() => runtime.login(), [runtime])
    const logout = useCallback(() => runtime.logout(), [runtime])
    const refresh = useCallback(
        (options?: { force?: boolean }) => runtime.refresh(options),
        [runtime],
    )

    return useMemo<UseAuthValue>(
        () => ({
            status: state.tag,
            state,
            user: userOf(state),
            roles: rolesOf(state),
            airports: airportsOf(state),
            isAuthenticated: state.tag === 'authenticated' || state.tag === 'refreshing',
            isBusy: BUSY_STATES.has(state.tag),
            session: sessionOf(state),
            getAccessToken,
            login,
            logout,
            refresh,
        }),
        [state, getAccessToken, login, logout, refresh],
    )
}
